import { CategoryId, NumberEntry, NumberType } from '../types/angelNumber';
import { curatedNumbers } from './curated';
import { generatedNumbers, pickDigits } from './families';

const uniq = <T,>(arr: T[]): T[] => Array.from(new Set(arr));

/** Lookup key used by every index: lowercase alphanumerics only. */
export function normalizeKey(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function mergeEntry(existing: NumberEntry, incoming: NumberEntry): void {
  existing.categories = uniq([...existing.categories, ...incoming.categories]);
  existing.searchAliases = uniq([
    ...existing.searchAliases,
    ...incoming.searchAliases,
    incoming.displayNumber,
    incoming.number,
  ]);
  existing.keywords = uniq([...existing.keywords, ...incoming.keywords]);
  existing.relatedNumbers = uniq([...existing.relatedNumbers, ...incoming.relatedNumbers]);
  existing.popularityScore = Math.max(existing.popularityScore, incoming.popularityScore);
  existing.isPopular = existing.isPopular || incoming.isPopular;
  existing.isFeatured = existing.isFeatured || incoming.isFeatured;
  existing.portalMeaning = existing.portalMeaning ?? incoming.portalMeaning;
  existing.twinFlameMeaning = existing.twinFlameMeaning ?? incoming.twinFlameMeaning;
  existing.numerologyMeaning = existing.numerologyMeaning ?? incoming.numerologyMeaning;
  existing.historicalSymbolism = existing.historicalSymbolism ?? incoming.historicalSymbolism;
  existing.affirmation = existing.affirmation ?? incoming.affirmation;
  existing.datePattern = existing.datePattern ?? incoming.datePattern;
}

/** One unified collection. Curated entries win; generated ones fill the gaps. */
function buildRegistry(): NumberEntry[] {
  const byId = new Map<string, NumberEntry>();

  curatedNumbers.forEach((entry) => byId.set(entry.id, { ...entry }));

  generatedNumbers.forEach((entry) => {
    const existing = byId.get(entry.id);
    if (existing) mergeEntry(existing, entry);
    else byId.set(entry.id, { ...entry });
  });

  const all = Array.from(byId.values());
  const ids = new Set(all.map((entry) => entry.id));

  // Guarantee 3–6 related numbers on every entry.
  all.forEach((entry) => {
    const [a, b] = pickDigits(entry.number || entry.id.replace(/\D/g, '') || '1');
    const candidates = [
      ...entry.relatedNumbers,
      String(a).repeat(3),
      String(a).repeat(4),
      String(a).repeat(2),
      String(b).repeat(3),
      '1111',
      '111',
      '222',
      '777',
    ];
    entry.relatedNumbers = uniq(candidates.filter((id) => ids.has(id) && id !== entry.id)).slice(0, 6);
  });

  return all.sort((x, y) => {
    if (x.type === 'portal_date' && y.type !== 'portal_date') return 1;
    if (y.type === 'portal_date' && x.type !== 'portal_date') return -1;
    if (x.number.length !== y.number.length) return x.number.length - y.number.length;
    return x.number.localeCompare(y.number);
  });
}

export const allNumbers: NumberEntry[] = buildRegistry();

export const numbersById: Record<string, NumberEntry> = allNumbers.reduce(
  (acc, entry) => {
    acc[entry.id] = entry;
    return acc;
  },
  {} as Record<string, NumberEntry>,
);

export const numbersBySlug: Record<string, NumberEntry> = allNumbers.reduce(
  (acc, entry) => {
    acc[entry.slug] = entry;
    return acc;
  },
  {} as Record<string, NumberEntry>,
);

/** Normalized numeric form → id (e.g. "1111" → "1111"). */
export const numberIndex: Record<string, string> = (() => {
  const index: Record<string, string> = {};
  allNumbers.forEach((entry) => {
    const key = normalizeKey(entry.number);
    if (key && !index[key]) index[key] = entry.id;
  });
  return index;
})();

/** Every alias ("11:11", "11 11", "eleven eleven") → id. */
export const aliasIndex: Record<string, string> = (() => {
  const index: Record<string, string> = {};
  allNumbers.forEach((entry) => {
    entry.searchAliases.forEach((alias) => {
      const key = normalizeKey(alias);
      if (!key) return;
      if (numberIndex[key] && numberIndex[key] !== entry.id) return; // never shadow a real number
      if (!index[key]) index[key] = entry.id;
    });
  });
  return index;
})();

export function getNumber(key: string | undefined): NumberEntry | undefined {
  if (!key) return undefined;
  if (numbersById[key]) return numbersById[key];
  if (numbersBySlug[key]) return numbersBySlug[key];
  const normalized = normalizeKey(key);
  const viaNumber = numberIndex[normalized];
  if (viaNumber) return numbersById[viaNumber];
  const viaAlias = aliasIndex[normalized];
  return viaAlias ? numbersById[viaAlias] : undefined;
}

export function numbersByCategory(category: CategoryId | 'all'): NumberEntry[] {
  if (category === 'all') return allNumbers;
  return allNumbers.filter((entry) => entry.categories.includes(category));
}

export function numbersByType(type: NumberType): NumberEntry[] {
  return allNumbers.filter((entry) => entry.type === type);
}

const byScore = (x: NumberEntry, y: NumberEntry) => y.popularityScore - x.popularityScore;

/** The eight classic three-digit sequences, ranked by popularity score. */
export const popularNumberIds: string[] = allNumbers
  .filter((entry) => entry.type === 'angel_number' && /^(\d)\1\1$/.test(entry.number))
  .sort(byScore)
  .slice(0, 8)
  .sort((x, y) => Number(x.number) - Number(y.number))
  .map((entry) => entry.id);

export const featuredNumberIds: string[] = allNumbers
  .filter((entry) => entry.isFeatured)
  .sort(byScore)
  .map((entry) => entry.id);

export const trendingNumberIds: string[] = allNumbers
  .filter((entry) => entry.isPopular && !entry.isFeatured)
  .sort(byScore)
  .slice(0, 6)
  .map((entry) => entry.id);

export const recommendedNumberIds: string[] = ['111', '222', '333'];

export const portalDates: NumberEntry[] = numbersByType('portal_date');
